import { useEffect, useMemo, useState } from "react";
import { Plus, Search, RefreshCw } from "lucide-react";
import PageHeader from "../../components/common/PageHeader";
import TaskStatusScreen from "../../components/common/TaskStatusScreen";
import TaskConfirmDialog from "../../components/common/TaskConfirmDialog";
import TaskTemplateModal from "../../components/task-templates/TaskTemplateModal";
import TaskTemplateTable from "../../components/task-templates/TaskTemplateTable";
import AddTaskModal from "../../components/tasks/AddTaskModal";
import {
  createTaskTemplate,
  deleteTaskTemplate,
  getAllTaskTemplates,
  getTaskTemplate,
  updateTaskTemplate,
  updateTaskTemplateStatus,
} from "../../api/taskTemplates";

export default function TaskTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [openTaskModal, setOpenTaskModal] = useState(false);
  const [taskTemplateSeed, setTaskTemplateSeed] = useState(null);

  const [confirmState, setConfirmState] = useState({
    open: false,
    type: null,
    template: null,
  });
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [pageError, setPageError] = useState("");
  const [toast, setToast] = useState({ type: "", message: "" });

  const activeOnlyValue = useMemo(() => {
    if (statusFilter === "active") return true;
    if (statusFilter === "inactive") return false;
    return undefined;
  }, [statusFilter]);

  const loadTemplates = async ({ silent = false } = {}) => {
    try {
      if (silent) setRefreshing(true);
      else setLoading(true);

      setPageError("");

      const response = await getAllTaskTemplates({
        activeOnly: activeOnlyValue,
      });

      const normalized = Array.isArray(response)
        ? response
        : response?.data || [];

      setTemplates(normalized);
    } catch (error) {
      console.error(error);
      setPageError(
        error?.response?.data?.message ||
          "Failed to load task templates. Please try again."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [activeOnlyValue]);

  useEffect(() => {
    if (!toast.message) return;
    const timer = setTimeout(() => setToast({ type: "", message: "" }), 2800);
    return () => clearTimeout(timer);
  }, [toast]);

  const filteredTemplates = useMemo(() => {
    const search = query.trim().toLowerCase();

    if (!search) return templates;

    return templates.filter((template) => {
      const source = [
        template.name,
        template.description,
        ...(template.checklistItems || []).map((item) => item.description),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return source.includes(search);
    });
  }, [templates, query]);

  const handleOpenCreate = () => {
    setModalMode("create");
    setSelectedTemplate(null);
    setOpenModal(true);
  };

  const handleOpenEdit = async (template) => {
    try {
      setSaving(true);
      const fullTemplate = await getTaskTemplate(template.id);

      setModalMode("edit");
      setSelectedTemplate(fullTemplate?.data || fullTemplate);
      setOpenModal(true);
    } catch (error) {
      console.error(error);
      setToast({
        type: "error",
        message:
          error?.response?.data?.message || "Failed to load template details.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCreateTask = async (template) => {
    try {
      setSaving(true);
      const fullTemplate = await getTaskTemplate(template.id);

      setTaskTemplateSeed(fullTemplate?.data || fullTemplate);
      setOpenTaskModal(true);
    } catch (error) {
      console.error(error);
      setToast({
        type: "error",
        message:
          error?.response?.data?.message ||
          "Failed to prepare task from template.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitModal = async (payload) => {
    try {
      setSaving(true);

      if (modalMode === "edit") {
        await updateTaskTemplate(payload);
        setToast({ type: "success", message: "Template updated successfully." });
      } else {
        await createTaskTemplate(payload);
        setToast({ type: "success", message: "Template created successfully." });
      }

      setOpenModal(false);
      setSelectedTemplate(null);
      await loadTemplates({ silent: true });
    } catch (error) {
      console.error(error);
      setToast({
        type: "error",
        message:
          error?.response?.data?.message ||
          "Unable to save the template. Please review the form and try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const openStatusConfirm = (template) => {
    setConfirmState({
      open: true,
      type: "status",
      template,
    });
  };

  const openDeleteConfirm = (template) => {
    setConfirmState({
      open: true,
      type: "delete",
      template,
    });
  };

  const closeConfirm = () => {
    if (confirmLoading) return;
    setConfirmState({
      open: false,
      type: null,
      template: null,
    });
  };

  const handleConfirmAction = async () => {
    const template = confirmState.template;
    if (!template) return;

    try {
      setConfirmLoading(true);

      if (confirmState.type === "status") {
        await updateTaskTemplateStatus({
          templateId: template.id,
          isActive: !template.isActive,
        });

        setToast({
          type: "success",
          message: template.isActive
            ? "Template deactivated successfully."
            : "Template activated successfully.",
        });
      }

      if (confirmState.type === "delete") {
        await deleteTaskTemplate(template.id);
        setToast({
          type: "success",
          message: "Template deleted successfully.",
        });
      }

      closeConfirm();
      await loadTemplates({ silent: true });
    } catch (error) {
      console.error(error);
      setToast({
        type: "error",
        message:
          error?.response?.data?.message ||
          "Action failed. Please try again.",
      });
    } finally {
      setConfirmLoading(false);
      setConfirmState({
        open: false,
        type: null,
        template: null,
      });
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <PageHeader
        title="Task Templates"
        subtitle="Build reusable task blueprints with structured checklist items for consistent field execution."
        action={
          <>
            <button
              onClick={() => loadTemplates({ silent: true })}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>

            <button
              onClick={handleOpenCreate}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
            >
              <Plus className="h-4 w-4" />
              New Template
            </button>
          </>
        }
      />

      {toast.message && (
        <div
          className={`rounded-2xl border px-4 py-3 text-sm font-medium ${
            toast.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-400"
              : "border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-400"
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_220px]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search templates, descriptions, or checklist items"
            className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-300 dark:border-slate-800 dark:bg-slate-900 dark:text-white"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-300 dark:border-slate-800 dark:bg-slate-900 dark:text-white"
        >
          <option value="all">All templates</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
      </div>

      {pageError ? (
        <TaskStatusScreen
          title="Unable to load templates"
          message={pageError}
          action={
            <button
              onClick={() => loadTemplates()}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white dark:bg-white dark:text-slate-900"
            >
              Try again
            </button>
          }
        />
      ) : filteredTemplates.length === 0 && !loading ? (
        <TaskStatusScreen
          title="No task templates found"
          message="Create your first reusable template to standardize operational workflows."
          action={
            <button
              onClick={handleOpenCreate}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white dark:bg-white dark:text-slate-900"
            >
              Create template
            </button>
          }
        />
      ) : (
        <TaskTemplateTable
          templates={filteredTemplates}
          loading={loading}
          onEdit={handleOpenEdit}
          onToggleStatus={openStatusConfirm}
          onDelete={openDeleteConfirm}
          onCreateTask={handleOpenCreateTask}
          actionLoading={saving}
        />
      )}

      <TaskTemplateModal
        open={openModal}
        mode={modalMode}
        initialData={selectedTemplate}
        loading={saving}
        onClose={() => {
          if (saving) return;
          setOpenModal(false);
          setSelectedTemplate(null);
        }}
        onSubmit={handleSubmitModal}
      />

      <AddTaskModal
        open={openTaskModal}
        onClose={() => {
          if (saving) return;
          setOpenTaskModal(false);
          setTaskTemplateSeed(null);
        }}
        initialTemplate={taskTemplateSeed}
        forceTemplateMode
      />

      <TaskConfirmDialog
        open={confirmState.open}
        loading={confirmLoading}
        title={
          confirmState.type === "delete"
            ? "Delete task template"
            : confirmState.template?.isActive
            ? "Deactivate task template"
            : "Activate task template"
        }
        message={
          confirmState.type === "delete"
            ? `Are you sure you want to delete "${confirmState.template?.name}"? This action cannot be undone.`
            : confirmState.template?.isActive
            ? `Deactivate "${confirmState.template?.name}"? It will remain available historically but should no longer be used for active workflows.`
            : `Activate "${confirmState.template?.name}" so it becomes available for use again.`
        }
        confirmText={
          confirmState.type === "delete"
            ? "Delete"
            : confirmState.template?.isActive
            ? "Deactivate"
            : "Activate"
        }
        confirmVariant={confirmState.type === "delete" ? "danger" : "default"}
        onConfirm={handleConfirmAction}
        onClose={closeConfirm}
      />
    </div>
  );
}